// LLM177 - "My Work" drawer.
// Briefcase icon in the AppShell header next to NotificationsBell. Lists
// every non-terminal analysis_run assigned to the current user. Polls the
// count on visibility/30s; full list fetches when the drawer opens.
//
// Replaces the originally-planned "My Locks + Assignments" drawer - the
// lock section was dropped along with the brand-lock retirement
// (2026-04-24 pivot). Ownership is carried by analysis_runs.assignee_user_id.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { analysisRunsList, fetchBrands } from "../services/api";

const POLL_MS = 30_000;

const STAGE_FOR_STATUS = {
  pending: 1,
  running_audit: 2,
  audit_complete: 2,
  running_analysis: 3,
  analysis_complete: 3,
  generating_pptx: 4,
  ready: 4,
  delivered: 4,
  failed: null,
};

function statusLabel(status) {
  return (status || "").replace(/_/g, " ");
}

export default function MyWorkDrawer() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [brandMap, setBrandMap] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const wrapRef = useRef(null);

  const refreshCount = useCallback(async () => {
    try {
      const res = await analysisRunsList({ assignee: "me", active: true });
      setCount((res?.items || []).length);
    } catch {
      // Silent
    }
  }, []);

  const refreshItems = useCallback(async () => {
    setLoading(true);
    try {
      const [runsRes, brandsRes] = await Promise.all([
        analysisRunsList({ assignee: "me", active: true }),
        fetchBrands().catch(() => []),
      ]);
      const runs = runsRes?.items || [];
      setItems(runs);
      setCount(runs.length);
      const bm = {};
      const list = Array.isArray(brandsRes) ? brandsRes : brandsRes?.brands || [];
      for (const b of list) bm[Number(b.id)] = b.name;
      setBrandMap(bm);
    } catch {
      // Silent
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll count
  useEffect(() => {
    refreshCount();
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshCount();
    };
    document.addEventListener("visibilitychange", onVisible);
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") refreshCount();
    }, POLL_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, [refreshCount]);

  // Fetch list when drawer opens
  useEffect(() => {
    if (open) refreshItems();
  }, [open, refreshItems]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const onResume = (run) => {
    setOpen(false);
    navigate(`/brands/${run.brand_id}/new-analysis?runId=${run.id}`);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={count ? `${count} run${count === 1 ? "" : "s"} assigned to you` : "My Work"}
        aria-label="My Work"
        style={{
          position: "relative",
          background: "rgba(255,255,255,0.12)",
          border: "1px solid rgba(255,255,255,0.2)",
          color: "#fff",
          width: 38,
          height: 38,
          borderRadius: 10,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
        {count > 0 && (
          <span
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              minWidth: 18,
              height: 18,
              padding: "0 5px",
              borderRadius: 9,
              background: "#ffcc33",
              color: "#111827",
              fontSize: 10,
              fontWeight: 900,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
              boxShadow: "0 0 0 2px rgba(0,0,0,0.35)",
            }}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 380,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: 520,
            background: "#fff",
            color: "#111827",
            borderRadius: 14,
            boxShadow: "0 16px 44px rgba(15,23,42,0.25)",
            border: "1px solid #e5e7eb",
            display: "flex",
            flexDirection: "column",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              padding: "12px 14px",
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ fontWeight: 900, fontSize: 14 }}>My Work</div>
            <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 700 }}>
              Assigned to you · in flight
            </div>
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading ? (
              <div style={{ padding: 18, color: "#6b7280", fontSize: 13, textAlign: "center" }}>
                Loading…
              </div>
            ) : items.length === 0 ? (
              <div style={{ padding: 24, color: "#9ca3af", fontSize: 13, textAlign: "center" }}>
                Nothing in your queue. Start a new analysis from the Brands Hub.
              </div>
            ) : (
              items.map((r) => {
                const stage = STAGE_FOR_STATUS[r.status];
                const brandName = brandMap[Number(r.brand_id)] || `Brand #${r.brand_id}`;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => onResume(r)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "12px 14px",
                      borderBottom: "1px solid #f3f4f6",
                      background: "#fff",
                      border: "none",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                      <div style={{ fontWeight: 800, fontSize: 13, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.title || `Run #${r.id}`}
                      </div>
                      {stage && (
                        <span
                          style={{
                            padding: "2px 8px",
                            borderRadius: 999,
                            background: "#eff6ff",
                            color: "#1e3a8a",
                            fontSize: 10,
                            fontWeight: 800,
                            border: "1px solid #bfdbfe",
                          }}
                        >
                          Stage {stage}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                      {brandName} · {statusLabel(r.status)}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
